import { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef, ReactNode } from 'react';
import { cartService } from '@/lib/services/cart.service';
import { productService } from '@/lib/services/product.service';
import { couponService, couponErrorFrom, CartQuote } from '@/lib/services/coupon.service';
import { useStore } from './StoreContext';
import { useAuth } from './AuthContext';
import type { CartItem, Coupon, Product } from '@/types';
import { toast } from 'sonner';

interface CartItemWithProduct extends CartItem {
  product: Product;
}

interface ApplyCouponResult {
  success: boolean;
  message: string;
}

interface CartContextType {
  items: CartItemWithProduct[];
  itemCount: number;
  subtotal: number;
  discount: number;
  total: number;
  coupon: Coupon | null;
  couponDiscount: number;
  loading: boolean;
  hasCreditItem: boolean;
  addItem: (productId: string, quantity?: number) => Promise<void>;
  addCredit: (amount: number) => Promise<void>;
  removeCredit: () => Promise<void>;
  removeVipUpgrade: () => Promise<void>;
  removeItem: (productId: string) => Promise<void>;
  updateQuantity: (productId: string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  applyCoupon: (code: string) => Promise<ApplyCouponResult>;
  removeCoupon: () => void;
  refreshCart: () => Promise<void>;
  /** Cotação do servidor em uso (null enquanto não carregou). */
  quote: CartQuote | null;
  /** Recalculando no servidor: o checkout espera terminar. */
  quoting: boolean;
  /** Recalcula a cotação agora (usado quando o servidor avisa que o total mudou). */
  refreshQuote: () => Promise<CartQuote | null>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

interface CartProviderProps {
  children: ReactNode;
}

export function CartProvider({ children }: CartProviderProps) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [coupon, setCoupon] = useState<Coupon | null>(null);
  const [quote, setQuote] = useState<CartQuote | null>(null);
  const [quoting, setQuoting] = useState(false);
  // Código do cupom lido no momento da cotação, sem depender de closure antiga.
  const couponCodeRef = useRef<string | null>(null);
  const quoteSeq = useRef(0);
  const quoteTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [loading, setLoading] = useState(false);
  const [productsCache, setProductsCache] = useState<Product[]>([]);
  const { store } = useStore();
  const { isAuthenticated } = useAuth();

  /**
   * Busca a cotação no servidor com o cupom atual.
   *
   * Roda depois de TODA mudança no carrinho. Se o cupom deixou de valer (compra
   * mínima, produto removido, limite), o servidor recusa: o cupom sai da tela,
   * o jogador é avisado e a cotação é refeita sem cupom.
   */
  const refreshQuote = useCallback(async (): Promise<CartQuote | null> => {
    if (!store?.id || !isAuthenticated) {
      setQuote(null);
      setQuoting(false);
      return null;
    }

    const seq = ++quoteSeq.current;
    setQuoting(true);
    try {
      const code = couponCodeRef.current;
      try {
        const result = await couponService.quote(store.id, code);
        if (seq === quoteSeq.current) setQuote(result);
        return result;
      } catch (error: any) {
        const couponError = couponErrorFrom(error);
        if (!couponError || !code) throw error;

        couponCodeRef.current = null;
        setCoupon(null);
        toast.error(couponError.message || 'O cupom não vale mais para este carrinho');

        const semCupom = await couponService.quote(store.id, null);
        if (seq === quoteSeq.current) setQuote(semCupom);
        return semCupom;
      }
    } catch (error) {
      console.error('Erro ao calcular o carrinho:', error);
      if (seq === quoteSeq.current) setQuote(null);
      return null;
    } finally {
      if (seq === quoteSeq.current) setQuoting(false);
    }
  }, [store?.id, isAuthenticated]);

  /**
   * Agenda a cotação. Cliques seguidos em +/- viram uma chamada só, e o
   * checkout fica bloqueado (quoting) desde o primeiro clique até a resposta.
   */
  const scheduleQuote = useCallback(() => {
    setQuoting(true);
    quoteSeq.current++;
    if (quoteTimer.current) clearTimeout(quoteTimer.current);
    quoteTimer.current = setTimeout(() => {
      quoteTimer.current = null;
      void refreshQuote();
    }, 300);
  }, [refreshQuote]);

  useEffect(() => () => {
    if (quoteTimer.current) clearTimeout(quoteTimer.current);
  }, []);

  // Carregar carrinho do backend quando o usuário logar; também atualiza o cache
  // de produtos para garantir preços frescos a cada sincronização
  const refreshCart = useCallback(async () => {
    if (!store?.id || !isAuthenticated) {
      setItems([]);
      return;
    }

    try {
      setLoading(true);
      const [cart, freshProducts] = await Promise.all([
        cartService.getCart(store.id),
        productService.listByStore(store.id),
      ]);
      setProductsCache(freshProducts);
      setItems(cart.items.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
        discountPercent: item.discountPercent || 0,
        priceWithDiscount: item.priceWithDiscount || item.price,
        type: item.type ?? 'PRODUCT',
        customAmount: item.customAmount ?? null,
        productName: item.productName,
        vipUpgrade: item.vipUpgrade,
      })));
      // Toda mudança no carrinho refaz a conta no servidor, cupom incluído.
      scheduleQuote();
    } catch (error) {
      console.error('Erro ao carregar carrinho:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [store?.id, isAuthenticated, scheduleQuote]);

  // Carregar carrinho ao montar e quando o usuário logar
  useEffect(() => {
    refreshCart();
  }, [refreshCart]);

  const addItem = useCallback(async (productId: string, quantity: number = 1) => {
    if (!store?.id || !isAuthenticated) {
      toast.error('Você precisa estar logado para adicionar itens ao carrinho');
      return;
    }

    try {
      await cartService.addItem(store.id, { productId, quantity });
      await refreshCart();
    } catch (error: any) {
      console.error('Erro ao adicionar item:', error);
      toast.error(error.response?.data?.message || 'Erro ao adicionar item ao carrinho');
    }
  }, [store?.id, isAuthenticated, refreshCart]);

  const addCredit = useCallback(async (amount: number) => {
    if (!store?.id || !isAuthenticated) {
      toast.error('Você precisa estar logado para comprar créditos');
      return;
    }
    try {
      await cartService.addCredit(store.id, amount);
      await refreshCart();
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Erro ao adicionar créditos ao carrinho';
      toast.error(msg);
      throw error;
    }
  }, [store?.id, isAuthenticated, refreshCart]);

  const removeCredit = useCallback(async () => {
    if (!store?.id || !isAuthenticated) return;
    try {
      await cartService.removeCredit(store.id);
      await refreshCart();
    } catch (error) {
      toast.error('Erro ao remover créditos do carrinho');
    }
  }, [store?.id, isAuthenticated, refreshCart]);

  const removeVipUpgrade = useCallback(async () => {
    if (!store?.id || !isAuthenticated) return;
    try {
      await cartService.removeVipUpgrade(store.id);
      await refreshCart();
    } catch (error) {
      console.error('Erro ao remover upgrade:', error);
    }
  }, [store?.id, isAuthenticated, refreshCart]);

  const removeItem = useCallback(async (productId: string) => {
    if (!store?.id || !isAuthenticated) {
      return;
    }

    try {
      await cartService.removeItem(store.id, productId);
      await refreshCart();
    } catch (error) {
      console.error('Erro ao remover item:', error);
      toast.error('Erro ao remover item do carrinho');
    }
  }, [store?.id, isAuthenticated, refreshCart]);

  const updateQuantity = useCallback(async (productId: string, quantity: number) => {
    if (!store?.id || !isAuthenticated) {
      return;
    }

    if (quantity <= 0) {
      await removeItem(productId);
      return;
    }

    try {
      await cartService.updateItemQuantity(store.id, productId, { quantity });
      await refreshCart();
    } catch (error) {
      console.error('Erro ao atualizar quantidade:', error);
      toast.error('Erro ao atualizar quantidade');
    }
  }, [store?.id, isAuthenticated, removeItem, refreshCart]);

  const clearCart = useCallback(async () => {
    if (!store?.id || !isAuthenticated) {
      return;
    }

    try {
      await cartService.clearCart(store.id);
      setItems([]);
      setCoupon(null);
      couponCodeRef.current = null;
      setQuote(null);
    } catch (error) {
      console.error('Erro ao limpar carrinho:', error);
      toast.error('Erro ao limpar carrinho');
    }
  }, [store?.id, isAuthenticated]);

  const applyCoupon = useCallback(async (code: string): Promise<ApplyCouponResult> => {
    if (!store?.id || !isAuthenticated) {
      return { success: false, message: 'Você precisa estar logado para aplicar cupons' };
    }

    if (items.length === 0) {
      return { success: false, message: 'Adicione itens ao carrinho antes de aplicar um cupom' };
    }

    const normalizado = code.trim().toUpperCase();

    try {
      // O servidor calcula sobre o carrinho salvo: nada de total vindo daqui.
      const result = await couponService.quote(store.id, normalizado);
      if (!result.coupon) {
        return { success: false, message: 'Cupom inválido' };
      }

      couponCodeRef.current = result.coupon.code;
      if (quoteTimer.current) {
        clearTimeout(quoteTimer.current);
        quoteTimer.current = null;
      }
      quoteSeq.current++;
      setQuote(result);
      setQuoting(false);
      setCoupon({
        code: result.coupon.code,
        type: result.coupon.discountType === 'PERCENTAGE' ? 'percentage' : 'fixed',
        value: result.couponDiscount,
      });
      return { success: true, message: `Cupom "${result.coupon.name}" aplicado com sucesso!` };
    } catch (error: any) {
      const couponError = couponErrorFrom(error);
      const message = couponError?.message || error.response?.data?.message || 'Erro ao validar cupom';
      return { success: false, message };
    }
  }, [store?.id, isAuthenticated, items.length]);

  const removeCoupon = useCallback(() => {
    couponCodeRef.current = null;
    setCoupon(null);
    void refreshQuote();
  }, [refreshQuote]);

  // Limpar cupom automaticamente quando o carrinho ficar vazio
  useEffect(() => {
    if (items.length === 0 && coupon !== null) {
      couponCodeRef.current = null;
      setCoupon(null);
    }
  }, [items.length, coupon]);

  // Mantém o valor exibido do cupom igual ao da última cotação.
  useEffect(() => {
    if (coupon && quote && coupon.value !== quote.couponDiscount) {
      setCoupon({ ...coupon, value: quote.couponDiscount });
    }
  }, [quote, coupon]);

  const cartItems = useMemo((): CartItemWithProduct[] => {
    return items.map(item => {
      if (item.type === 'VIP_UPGRADE') {
        const amount = item.customAmount ?? item.priceWithDiscount ?? 0;
        const syntheticProduct = {
          id: 'vip-upgrade',
          name: item.productName || 'Upgrade VIP',
          slug: 'vip-upgrade',
          description: '',
          price: amount,
          imageUrl: '',
          stock: null as any,
          category: null as any,
        } as unknown as Product;
        return { ...item, product: syntheticProduct } as CartItemWithProduct;
      }
      if (item.type === 'CREDIT') {
        const amount = item.customAmount ?? item.priceWithDiscount ?? 0;
        const syntheticProduct: Product = {
          id: 'credit-topup',
          name: 'Adicionar Créditos',
          slug: 'credit-topup',
          description: '',
          price: amount,
          imageUrl: '',
          stock: null as any,
          category: null as any,
        } as unknown as Product;
        return { ...item, product: syntheticProduct } as CartItemWithProduct;
      }
      const product = productsCache.find(p => p.id === item.productId);
      return { ...item, product: product! };
    }).filter(item => item.product);
  }, [items, productsCache]);

  const hasCreditItem = useMemo(
    () => cartItems.some(i => i.type === 'CREDIT'),
    [cartItems],
  );

  const localSubtotal = useMemo((): number => {
    return cartItems.reduce((sum, item) => {
      if (item.type === 'CREDIT' || item.type === 'VIP_UPGRADE') {
        return sum + (item.customAmount ?? item.product?.price ?? 0);
      }
      const itemPrice = item.product?.price || 0;
      return sum + itemPrice * item.quantity;
    }, 0);
  }, [cartItems]);

  const subtotal = quote ? quote.subtotal : localSubtotal;

  // Calcular desconto do admin (economia)
  const adminDiscount = useMemo((): number => {
    return cartItems.reduce((sum, item) => {
      if (item.discountPercent && item.discountPercent > 0 && item.product && item.priceWithDiscount) {
        const originalPrice = item.product.price * item.quantity;
        const discountedPrice = item.priceWithDiscount * item.quantity;
        return sum + (originalPrice - discountedPrice);
      }
      return sum;
    }, 0);
  }, [cartItems]);

  // Os números exibidos vêm da cotação do servidor. A conta local só aparece
  // no instante antes da primeira cotação chegar.
  const couponDiscount = quote?.couponDiscount ?? 0;

  const discount = useMemo((): number => {
    return quote ? quote.discount : adminDiscount;
  }, [quote, adminDiscount]);

  const total = useMemo((): number => {
    return quote ? quote.total : Math.max(0, subtotal - discount);
  }, [quote, subtotal, discount]);

  const itemCount = useMemo((): number => {
    return items.reduce((sum, item) => sum + item.quantity, 0);
  }, [items]);

  const value: CartContextType = {
    items: cartItems,
    itemCount,
    subtotal,
    discount,
    total,
    coupon,
    couponDiscount,
    loading,
    hasCreditItem,
    addItem,
    addCredit,
    removeCredit,
    removeVipUpgrade,
    removeItem,
    updateQuantity,
    clearCart,
    applyCoupon,
    removeCoupon,
    refreshCart,
    quote,
    quoting,
    refreshQuote,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

/**
 * Só no editor do construtor: o carrinho aparece cheio, com produtos da
 * própria loja, para o lojista ver e personalizar as caixas dele. O total sai
 * dos preços dos produtos; nada vai para o servidor e mexer no carrinho não
 * faz nada.
 */
export function CarrinhoDeAmostra({ produtos, children }: { produtos: Product[]; children: ReactNode }) {
  const pai = useContext(CartContext);
  const value = useMemo((): CartContextType | null => {
    if (!pai) return null;
    const escolhidos = produtos.slice(0, 2);
    const itens = escolhidos.map((product, i) => ({ productId: product.id, quantity: i + 1, product }));
    const linhas = itens.map((it) => ({ productId: it.productId, quantity: it.quantity, regularUnitPrice: it.product.price, unitPrice: it.product.price, promotional: false, total: it.product.price * it.quantity }));
    const subtotal = linhas.reduce((soma, l) => soma + l.total, 0);
    const nada = async () => {};
    return {
      ...pai,
      items: itens,
      itemCount: itens.reduce((soma, it) => soma + it.quantity, 0),
      subtotal,
      discount: 0,
      total: subtotal,
      coupon: null,
      couponDiscount: 0,
      loading: false,
      hasCreditItem: false,
      addItem: nada,
      addCredit: nada,
      removeCredit: nada,
      removeItem: nada,
      updateQuantity: nada,
      clearCart: nada,
      applyCoupon: async () => ({ success: false, message: '' }),
      removeCoupon: () => {},
      refreshCart: nada,
      quote: { lines: linhas, problems: [], subtotal, itemDiscount: 0, couponDiscount: 0, discount: 0, total: subtotal, coupon: null },
      quoting: false,
      refreshQuote: async () => null,
    };
  }, [pai, produtos]);
  if (!value) return <>{children}</>;
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}

export default CartContext;
